'use client';

import { useEffect, useState } from 'react';
import {
    GetAutoshipPromo,
    useIsExperimentFullSolution,
    usePageLayout,
    type ProductPromotion,
} from '@libs/common-next';
import type { Nullable } from '@libs/core';
import { getProductPromotions } from '@libs/ocapi/server';
import type { EligiblePromotion } from '@libs/common-types';
import type { AutoshipHeaderProps } from './types';

const getAutoshipPromo = (promotions?: Array<EligiblePromotion>) => {
    const autoshipPromos = promotions?.filter(
        (promo: EligiblePromotion) => promo?.type === 'autoship'
    );
    if (!autoshipPromos?.length) {
        return null;
    }
    // if there is only 1 autoship promo, return it
    if (autoshipPromos.length === 1) {
        return autoshipPromos[0];
    }
    // there are multiple autoship promos: return first non-default promo
    const nonDefaultPromos = autoshipPromos.filter(promo => !promo.default);

    // in case of scenerio where there are multiple default but no non-default, return first default promo
    return nonDefaultPromos?.length ? nonDefaultPromos[0] : autoshipPromos[0];
};

export default function AutoshipHeader({
    eligiblePromotions,
    productId,
    userHasSubscriptions,
}: Readonly<AutoshipHeaderProps>) {
    const { ocapi } = usePageLayout();
    const [autoshipPromoSFCC, setAutoshipPromoSFCC] =
        useState<Nullable<ProductPromotion>>(null);
    const [autoshipMessageCT, setAutoshipMessageCT] = useState('');

    // TODO: if checking OCAPI, and if available, check customer.registered && customer.authenticated
    const isAuthenticated =
        ocapi.data?.session?.customer?.auth_type === 'registered';

    const isFullSolution = useIsExperimentFullSolution();

    // SFCC promotions only
    useEffect(() => {
        const fetchPromotions = async () => {
            const promo = GetAutoshipPromo(
                await getProductPromotions(productId, isFullSolution)
            );

            setAutoshipPromoSFCC(promo);
        };

        if (!ocapi.isLoading && !isFullSolution) {
            fetchPromotions();
        }
    }, [productId, ocapi.isLoading, isAuthenticated, isFullSolution]);

    // CT Promotions
    useEffect(() => {
        if (!isFullSolution) {
            return;
        }

        const autoshipPromo = getAutoshipPromo(eligiblePromotions);
        if (autoshipPromo?.discountDisplay) {
            const message = userHasSubscriptions
                ? ' & Save 5%'
                : ` & Save ${autoshipPromo.discountDisplay}`;
            setAutoshipMessageCT(message);
        }
    }, [eligiblePromotions, isFullSolution, userHasSubscriptions]);

    const getMessage = (promo: Nullable<ProductPromotion>) => {
        let msg;
        if (userHasSubscriptions && promo && promo.autoShipCTA) {
            // For logged in users, ignore the autoshipPromotionContent and always show this message
            msg = 'Autoship & save 5%';
        } else if (promo && promo.autoShipCTA) {
            // For guests, show the actual autoShipCTA
            msg = promo.autoShipCTA;
        } else {
            // If there is no autoship CTA, just show this
            msg = 'Autoship';
        }

        return msg;
    };

    return (
        <span className="radio-field-title sparky-u-theme-typography-body-lg-bold">
            {isFullSolution
                ? `Autoship${autoshipMessageCT}`
                : getMessage(autoshipPromoSFCC)}
        </span>
    );
}
